import { useState, useMemo, useEffect } from "react";
import { Wallet, TrendingUp, CheckCircle2, Clock, ChevronDown, Download } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useClinic } from "@/store/ClinicContext";
import { generateDoctorExtrato } from "@/utils/receipt";
import { formatBRL, convenioRepasse } from "@/data/mockData";

const KpiCard = ({ icon: Icon, tint, label, value, sub }) => (
  <div className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
    <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${tint}`}>
      <Icon className="h-5 w-5" strokeWidth={2.2} />
    </div>
    <p className="mt-4 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
    <p className="font-mono-num mt-1 text-2xl font-bold text-slate-900">{value}</p>
    {sub && <p className="mt-0.5 text-xs text-slate-500">{sub}</p>}
  </div>
);

export const DoctorPortal = ({ doctors, lockedDoctorId }) => {
  const { appointments, healthPlans } = useClinic();
  const [selectedId, setSelectedId] = useState(lockedDoctorId || doctors[0]?.id);

  // Se o médico selecionado for removido, volta para o primeiro
  useEffect(() => {
    if (lockedDoctorId) {
      setSelectedId(lockedDoctorId);
    } else if (!doctors.find((d) => d.id === selectedId)) {
      setSelectedId(doctors[0]?.id);
    }
  }, [doctors, selectedId, lockedDoctorId]);

  const doctor = doctors.find((d) => d.id === selectedId) || doctors[0];

  const data = useMemo(() => {
    if (!doctor) return null;
    const planByName = (name) =>
      healthPlans.find((p) => p.name === name) || healthPlans.find((p) => p.name === "Particular");
    const mine = appointments
      .filter((a) => a.doctorId === doctor.id)
      .map((a) => {
        const _convenio = a.convenio || "Particular";
        const _repasse = convenioRepasse(planByName(_convenio), a.value) - (a.op_fee || 0);
        return { ...a, _convenio, _repasse };
      });
    const bruto = mine.reduce((s, a) => s + (a.value || 0), 0);
    const repasseLiquido = mine.reduce((s, a) => s + a._repasse, 0);
    const jaRecebido = mine.filter((a) => a.paid).reduce((s, a) => s + a._repasse, 0);
    const taxas = mine.reduce((s, a) => s + (a.op_fee || 0), 0);
    return {
      mine,
      bruto,
      taxas,
      repasseLiquido,
      jaRecebido,
      pendente: repasseLiquido - jaRecebido,
    };
  }, [appointments, doctor, healthPlans]);

  if (!doctor) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 py-16 text-center text-sm text-slate-400">
        Nenhum médico cadastrado. Cadastre um médico na aba Admin.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Selector */}
      <div className="flex flex-col gap-3 rounded-2xl border border-slate-200/70 bg-white p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)] sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          {doctor.avatar ? (
            <img src={doctor.avatar} alt={doctor.name} className="h-11 w-11 rounded-full object-cover ring-1 ring-slate-200" />
          ) : (
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-50 text-blue-600 ring-1 ring-blue-100">
              {doctor.name.charAt(0)}
            </div>
          )}
          <div>
            <p className="font-heading text-base font-bold text-slate-900">{doctor.name}</p>
            <p className="text-xs text-slate-500">
              {doctor.specialty} · CRM {doctor.crm}-{doctor.uf} · repasse{" "}
              <span className="font-semibold text-emerald-600">por convênio</span>
            </p>
          </div>
        </div>

        {!lockedDoctorId && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="portal-doctor-selector"
              className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50"
            >
              Trocar médico
              <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            {doctors.map((d) => (
              <DropdownMenuItem
                key={d.id}
                data-testid={`portal-doctor-option-${d.id}`}
                onClick={() => setSelectedId(d.id)}
                className="cursor-pointer gap-3 py-2"
              >
                <div className="flex flex-col">
                  <span className="text-sm font-semibold text-slate-800">{d.name}</span>
                  <span className="text-xs text-slate-400">
                    {d.specialty} · {d.repasse}%
                  </span>
                </div>
                {d.id === doctor.id && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-blue-600" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          icon={TrendingUp}
          tint="bg-blue-50 text-blue-600"
          label="Faturamento Bruto"
          value={formatBRL(data.bruto)}
          sub={`${data.mine.length} atendimentos`}
        />
        <div data-testid="portal-repasse-liquido" className="rounded-2xl border border-emerald-200/60 bg-emerald-50/50 p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600">
            <Wallet className="h-5 w-5" strokeWidth={2.2} />
          </div>
          <p className="mt-4 text-[11px] font-semibold uppercase tracking-wide text-emerald-600">
            Seu Repasse Líquido
          </p>
          <p className="font-mono-num mt-1 text-2xl font-bold text-emerald-700">
            {formatBRL(data.repasseLiquido)}
          </p>
          <p className="mt-0.5 text-xs text-emerald-600">conforme convênio · após glosa</p>
          <p data-testid="portal-op-fee-note" className="mt-1 text-[11px] text-emerald-700/80">Pagamentos online: taxa operacional de R$10,50 por pagamento realizado{data.taxas ? ` · descontado: ${formatBRL(data.taxas)}` : ""}.</p>
        </div>
        <KpiCard
          icon={CheckCircle2}
          tint="bg-slate-100 text-slate-600"
          label="Já Recebido"
          value={formatBRL(data.jaRecebido)}
          sub="repasse sobre pagamentos"
        />
        <KpiCard
          icon={Clock}
          tint="bg-amber-50 text-amber-600"
          label="Pendente"
          value={formatBRL(data.pendente)}
          sub="a receber"
        />
      </div>

      {/* Extrato */}
      <div className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h3 className="font-heading text-base font-bold text-slate-900">Extrato Detalhado</h3>
            <p className="text-xs text-slate-500">Atendimentos vinculados a {doctor.name}</p>
          </div>
          <button
            data-testid="portal-export-pdf"
            onClick={() => generateDoctorExtrato(doctor, data)}
            disabled={data.mine.length === 0}
            className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Download className="h-3.5 w-3.5 text-blue-600" />
            Baixar meu extrato PDF
          </button>
        </div>

        {data.mine.length === 0 ? (
          <div className="py-14 text-center text-sm text-slate-400">
            Nenhum atendimento vinculado a este médico ainda.
          </div>
        ) : (
          <>
            <div className="hidden grid-cols-[80px_1fr_120px_130px_130px] gap-4 border-b border-slate-100 bg-slate-50/60 px-6 py-3 lg:grid">
              {["Horário", "Paciente", "Valor", "Status", "Valor Líquido"].map((h, i) => (
                <span key={h} className={`text-[11px] font-semibold uppercase tracking-wide text-slate-400 ${i >= 2 ? "text-right" : ""}`}>
                  {h}
                </span>
              ))}
            </div>
            <div className="divide-y divide-slate-100">
              {data.mine.map((a) => (
                <div
                  key={a.id}
                  data-testid={`portal-extrato-row-${a.id}`}
                  className="grid grid-cols-1 gap-2 px-6 py-3.5 lg:grid-cols-[80px_1fr_120px_130px_130px] lg:items-center"
                >
                  <span className="font-mono-num text-sm font-bold text-slate-900">{a.time}</span>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{a.patient}</p>
                    <p className="text-xs text-slate-400">{a.procedure} · {a._convenio}</p>
                  </div>
                  <span className="font-mono-num text-sm text-slate-600 lg:text-right">
                    {formatBRL(a.value)}
                  </span>
                  <div className="lg:text-right">
                    {a.paid ? (
                      <span className="inline-flex rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                        Pago
                      </span>
                    ) : (
                      <span className="inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">
                        Pendente
                      </span>
                    )}
                  </div>
                  <span className="font-mono-num text-sm font-bold text-emerald-600 lg:text-right">
                    {formatBRL(a._repasse)}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
